import React from 'react';
import PropTypes from 'prop-types';
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Text from "react-native-web/dist/exports/Text";
import View from "react-native-web/dist/exports/View";
import StackView from '../StackView';
import Icon from '../Icon';
import { a11yProps, getTokensPropType, selectSystemProps, variantProp, viewProps } from '../utils';
import { applyTextStyles, useTheme } from '../ThemeProvider';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectCompletedIconTokens = _ref => {
  let {
    completedIconColor,
    completedIconSize
  } = _ref;
  return {
    size: completedIconSize,
    color: completedIconColor
  };
};
const selectConnectorStyles = (_ref2, isCompleted) => {
  let {
    connectorColor,
    connectorHeight,
    connectorMinWidth,
    connectorCompletedHeight,
    connectorCompletedColor
  } = _ref2;
  return {
    backgroundColor: connectorColor,
    height: connectorHeight,
    minWidth: connectorMinWidth,
    ...(isCompleted && {
      height: connectorCompletedHeight,
      backgroundColor: connectorCompletedColor
    })
  };
};
const selectCurrentInnerStyles = _ref3 => {
  let {
    knobCurrentInnerColor,
    knobCurrentInnerSize
  } = _ref3;
  return {
    backgroundColor: knobCurrentInnerColor,
    borderRadius: knobCurrentInnerSize / 2,
    height: knobCurrentInnerSize,
    width: knobCurrentInnerSize
  };
};
const selectKnobStyles = (_ref4, isCompleted, isCurrent) => {
  let {
    knobBackgroundColor,
    knobBorderColor,
    knobBorderWidth,
    knobCompletedBackgroundColor,
    knobCompletedBorderColor,
    knobCompletedPaddingLeft,
    knobCompletedPaddingTop,
    knobCurrentBackgroundColor,
    knobCurrentBorderColor,
    knobCurrentBorderWidth,
    knobCurrentPaddingLeft,
    knobCurrentPaddingTop,
    knobSize
  } = _ref4;
  return {
    backgroundColor: knobBackgroundColor,
    borderColor: knobBorderColor,
    borderRadius: knobSize / 2,
    borderWidth: knobBorderWidth,
    height: knobSize,
    width: knobSize,
    ...(isCompleted && {
      backgroundColor: knobCurrentBackgroundColor,
      borderColor: knobCurrentBorderColor,
      borderWidth: knobCurrentBorderWidth,
      paddingLeft: knobCurrentPaddingLeft,
      paddingTop: knobCurrentPaddingTop
    }),
    ...(isCurrent && {
      backgroundColor: knobCompletedBackgroundColor,
      borderColor: knobCompletedBorderColor,
      borderWidth: knobCurrentBorderWidth,
      paddingLeft: knobCompletedPaddingLeft,
      paddingTop: knobCompletedPaddingTop
    })
  };
};
const selectLabelContainerStyles = _ref5 => {
  let {
    labelDirection,
    labelGap,
    labelMarginTop,
    labelPaddingLeft,
    labelPaddingRight
  } = _ref5;
  return {
    marginTop: labelMarginTop,
    paddingLeft: labelPaddingLeft,
    paddingRight: labelPaddingRight,
    flexDirection: labelDirection,
    gap: labelGap
  };
};
const selectStepLabelStyles = (_ref6, themeOptions, isCurrent) => {
  let {
    stepLabelColor,
    labelCurrentColor,
    stepLabelFontWeight,
    stepLabelFontSize,
    stepLabelFontName,
    stepLabelLineHeight
  } = _ref6;
  return applyTextStyles({
    color: isCurrent ? labelCurrentColor : stepLabelColor,
    fontSize: stepLabelFontSize,
    lineHeight: stepLabelLineHeight,
    fontWeight: stepLabelFontWeight,
    fontName: stepLabelFontName,
    themeOptions
  });
};
const selectLabelStyles = (_ref7, themeOptions, isCurrent) => {
  let {
    labelColor,
    labelCurrentColor,
    labelCurrentFontWeight,
    labelFontSize,
    labelFontWeight,
    labelFontName,
    labelLineHeight
  } = _ref7;
  return applyTextStyles({
    color: isCurrent ? labelCurrentColor : labelColor,
    fontSize: labelFontSize,
    lineHeight: labelLineHeight,
    fontWeight: isCurrent ? labelCurrentFontWeight : labelFontWeight,
    fontName: labelFontName,
    themeOptions
  });
};
const getStepTestID = (isCompleted, isCurrent) => {
  let testID = 'StepTracker-Step';
  if (isCompleted) {
    testID += '-Completed';
  } else if (isCurrent) {
    testID += '-Current';
  }
  return testID;
};
const selectBarContainerStyles = (themeTokens, isCompleted, isCurrent) => ({
  backgroundColor: isCompleted ? themeTokens.barCompletedBackgroundColor : themeTokens.barBackgroundColor,
  height: themeTokens.barHeight,
  ...(isCurrent && {
    backgroundColor: themeTokens.barCurrentBackgroundColor
  })
});

/**
 * A single step of a StepTracker.
 */
const Step = /*#__PURE__*/React.forwardRef((_ref8, ref) => {
  let {
    label,
    name,
    status = 0,
    stepCount = 0,
    stepIndex = 0,
    tokens,
    isBarVariant,
    ...rest
  } = _ref8;
  const {
    completedIcon,
    showStepLabel,
    showStepName,
    textStepTrackerLabel,
    ...themeTokens
  } = tokens;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === stepCount - 1;
  const isCompleted = status > stepIndex;
  const isCurrent = status === stepIndex;
  const isActive = isCompleted || isCurrent;
  const {
    themeOptions
  } = useTheme();
  return /*#__PURE__*/_jsxs(StackView, {
    space: 0,
    tokens: {
      alignItems: 'stretch',
      flexGrow: 1
    },
    accessibilityLabel: label,
    accessibilityLevel: 2,
    accessibilityCurrent: status === stepIndex,
    ref: ref,
    ...selectProps(rest),
    children: [isBarVariant && /*#__PURE__*/_jsx(View, {
      style: selectBarContainerStyles(themeTokens, isCompleted, isCurrent),
      testID: getStepTestID(isCompleted, isCurrent)
    }), !isBarVariant && /*#__PURE__*/_jsxs(StackView, {
      direction: "row",
      space: 0,
      tokens: {
        alignItems: 'center',
        flexGrow: 0,
        justifyContent: 'center'
      },
      children: [/*#__PURE__*/_jsx(View, {
        style: [staticStyles.connector, !isFirst && selectConnectorStyles(themeTokens, isActive)]
      }), /*#__PURE__*/_jsxs(View, {
        style: [staticStyles.knob, selectKnobStyles(themeTokens, isCompleted, isCurrent)],
        testID: getStepTestID(isCompleted, isCurrent),
        children: [isCompleted && completedIcon && /*#__PURE__*/_jsx(Icon, {
          icon: completedIcon,
          tokens: selectCompletedIconTokens(themeTokens)
        }), (isCurrent && completedIcon || isCurrent && !completedIcon) && /*#__PURE__*/_jsx(View, {
          style: selectCurrentInnerStyles(themeTokens)
        })]
      }), /*#__PURE__*/_jsx(View, {
        style: [staticStyles.connector, !isLast && selectConnectorStyles(themeTokens, isCompleted)]
      })]
    }), showStepLabel && /*#__PURE__*/_jsxs(View, {
      style: [staticStyles.stepLabelContainer, selectLabelContainerStyles(tokens)],
      children: [showStepName && /*#__PURE__*/_jsx(Text, {
        style: [staticStyles.centeredText, selectStepLabelStyles(tokens, themeOptions, isCurrent)],
        children: name
      }), /*#__PURE__*/_jsx(StackView, {
        direction: "row",
        children: /*#__PURE__*/_jsx(Text, {
          style: [staticStyles.centeredText, tokens.labelDirection === 'column' && staticStyles.wrappingLabel, selectLabelStyles(tokens, themeOptions, isCurrent)],
          children: label
        })
      })]
    })]
  });
});
Step.displayName = 'Step';
Step.propTypes = {
  ...selectedSystemPropTypes,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  status: PropTypes.number.isRequired,
  stepCount: PropTypes.number.isRequired,
  stepIndex: PropTypes.number.isRequired,
  tokens: getTokensPropType('StepTracker'),
  variant: variantProp.propType
};
export default Step;
const staticStyles = StyleSheet.create({
  centeredText: {
    textAlign: 'center'
  },
  connector: {
    flex: 1,
    height: 1
  },
  completedKnob: {
    backgroundColor: 'transparent',
    textAlign: 'center'
  },
  knob: {
    borderWidth: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  stepLabelContainer: {
    justifyContent: 'center'
  },
  wrappingLabel: {
    width: 0,
    flex: 1
  }
});